import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, Input, Select, DatePicker, Button } from "antd";
import { FindAllInterviewSchedulesFilters } from "../../Interface";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { debounce } from "lodash";

interface FilterBarProps {
  filters: FindAllInterviewSchedulesFilters;
  filterOptions: {
    jobPosts: { _id: string; title: string }[];
    interviewers: { _id: string; name: string }[];
  };
  updateFilters: (
    key: keyof FindAllInterviewSchedulesFilters,
    value: string | null
  ) => void;
  clearFilters: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  filterOptions,
  updateFilters,
  clearFilters,
}) => {
  const [searchText, setSearchText] = useState<string | null>(filters.search);
  const organizerFilterOptions = filterOptions.interviewers.map(
    (interviewer) => ({
      label: interviewer.name,
      value: interviewer._id,
    })
  );

  const jobPostFilterOptions = filterOptions.jobPosts.map((jobPost) => ({
    label: jobPost.title,
    value: jobPost._id,
  }));

  const statusFilterOptions = ["upcoming", "completed"].map((status) => ({
    label: status,
    value: status,
  }));

  const handleStatusFilter = (value: string | undefined) => {
    updateFilters("status", value || null);
  };

  const handleJobPostFilter = (value: string | undefined) => {
    updateFilters("jobPost", value || null);
  };

  const handleOrganizerFilter = (value: string | undefined) => {
    updateFilters("organizer", value || null);
  };

  const handleDateFilter = (date: dayjs.Dayjs | null) => {
    updateFilters("date", date ? date.format("YYYY-MM-DD") : null);
  };

  const handleClearFilters = () => {
    setSearchText(null);
    clearFilters();
  };

  const debouncedSearchFilter = useMemo(
    () =>
      debounce((value: string | null) => {
        updateFilters("search", value);
      }, 500),
    [updateFilters]
  );

  useEffect(() => {
    debouncedSearchFilter(searchText);
    return () => {
      debouncedSearchFilter.cancel();
    };
  }, [searchText]);

  return (
    <div style={{ width: "100%", marginBottom: "16px" }}>
      <Row gutter={[12, 12]} align="middle" wrap style={{ width: "100%" }}>
        {/* Date Picker - Equal width with other filters */}
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <DatePicker
            style={{ width: "100%" }}
            allowClear
            // disabledDate={(current) =>
            //   current && current < dayjs().startOf("day")
            // }
            onChange={handleDateFilter}
            value={filters?.date ? dayjs(filters.date) : null}
          />
        </Col>

        {/* Search Input - Full width on mobile, equal width on larger screens */}
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Input
            placeholder="Search by name, email, or position"
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: "100%" }}
            value={searchText || ""}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>

        {/* Team Member Filter - Equal width with other filters */}
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            onChange={handleOrganizerFilter}
            placeholder="Filter by organizer"
            style={{ width: "100%" }}
            value={
              organizerFilterOptions.find(
                (organizer) => organizer.value === filters?.organizer
              )?.label
            }
            options={organizerFilterOptions}
            allowClear
          />
        </Col>

        {/* Status Filter - Equal width with other filters */}
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            onChange={handleStatusFilter}
            placeholder="Filter by status"
            style={{ width: "100%" }}
            value={filters?.status}
            options={statusFilterOptions}
            allowClear
          />
        </Col>

        {/* Position Filter - Equal width with other filters */}
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Select
            onChange={handleJobPostFilter}
            placeholder="Filter by position"
            style={{ width: "100%" }}
            value={filters?.jobPost}
            options={jobPostFilterOptions}
            allowClear
            dropdownStyle={{ whiteSpace: "normal" }}
            optionLabelProp="label" 
            optionRender={(option) => (
              <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                {option.label}
              </div>
            )}
            showSearch
            optionFilterProp="label"
          />
        </Col>

        {/* Clear All Button - Equal width with other filters */}
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Button
            type="default"
            icon={<ClearOutlined />}
            style={{ width: "100%" }}
            onClick={handleClearFilters}
          >
            Clear All
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default FilterBar;
